import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import type { ResultsRowsResponse } from "@containers/Hermes/types";

export const PAGE_SIZES = [25, 50, 100] as const;

// Pages already read, kept for the session by token + window, so paging back and
// reopening the panel do not query Elasticsearch again.
const pageCache = new Map<string, ResultsRowsResponse>();

type State = { data: ResultsRowsResponse | null; loading: boolean; error: string };

/**
 * One page of the rows behind an offered download (POST downloads/rows). The
 * request carries the offer's signed spec_token, so the rows come from the same
 * query the table, the count and the file use. The total is asked for with the
 * first page only, and only when the offer did not already carry it.
 */
export default function useResultsRows(specToken: string, page: number, pageSize: number, needTotal: boolean) {
  const [state, setState] = useState<State>({ data: null, loading: true, error: "" });
  const [attempt, setAttempt] = useState(0);
  const total = useRef<number | null>(null);

  useEffect(() => {
    const key = `${specToken}:${page}:${pageSize}`;
    const cached = pageCache.get(key);
    if (cached) {
      if (cached.total !== undefined) total.current = cached.total;
      setState({ data: cached, loading: false, error: "" });
      return;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    axios
      .post(getApiUrl("downloads/rows"), { spec_token: specToken, page, page_size: pageSize, with_total: needTotal && total.current === null })
      .then(({ data }) => {
        const payload: ResultsRowsResponse = data?.data;
        if (payload.total !== undefined) total.current = payload.total;
        pageCache.set(key, payload);
        if (!cancelled) setState({ data: payload, loading: false, error: "" });
      })
      .catch((err) => {
        const detail = err?.response?.data?.message;
        if (!detail) Sentry.captureException(err);
        if (!cancelled) setState((previous) => ({ ...previous, loading: false, error: detail || "The records could not be loaded. Please try again." }));
      });
    return () => {
      cancelled = true;
    };
  }, [specToken, page, pageSize, needTotal, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, total: total.current ?? state.data?.total ?? null, retry };
}
